import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Stack } from '@tanium/react-stack';
import { TextInput } from '@tanium/react-text-input';
import { Checkbox } from '@tanium/react-checkbox';
import { StackedRibbonListMinimal, StackedRibbonListWrapper } from '@tanium/react-stacked-ribbon-list';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { CircleInfoIcon } from '@tanium/react-graphics';
import { HEX_646A7E } from '@tanium/colors';
import { LinkText } from '../LinkText';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { MediumSubtleText } from '../../TextStyles';
import { IdentityProviderGroupDescription } from './IdentityProviderGroupDescription';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { DocLinks } from '../../utilities/constants';

interface RenderGroupAttributeProps {
  selectedRoleIds: Set<string>;
  setSelectedRoleIds: (selectedSuiteIds: Set<string>) => void;
  roleMapperData: ConfigureIdpRequest['roleMappers'];
  setRoleMapperData: (roleMapperData: ConfigureIdpRequest['roleMappers']) => void;
}

export type UserRolesRow = {
  userRole: string;
  selected: JSX.Element;
  schema: any;
  value: any;
};

// Define the mapping object
const roleMapping: { [key: string]: string } = {
  audit_user: 'Artifacts User',
  admin: 'Admin',
};

// Function to map roles
const mapRole = (role: string): string => {
  return roleMapping[role] || 'Unknown Role';
};

export const RenderGroupAttribute: React.VFC<RenderGroupAttributeProps> = ({
  selectedRoleIds,
  setSelectedRoleIds,
  roleMapperData,
  setRoleMapperData,
}) => {
  const { t } = useTranslation();
  const [displayRows, setDisplayRows] = useState<UserRolesRow[]>([]);
  const { idpRoles } = useIdentityProviderConfigs();
  const displayColumns = [
    {
      label: t('User Role'),
      dataKey: 'userRole',
      primary: true,
      width: '15%',
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {t('Claim Attribute Name')}
          <TooltippedIconButton
            data-testid="add-schema-tooltip"
            label={t('Add Schema')}
            tooltipContent={t('Group Claim Attribute')}
            height={16}
            width={16}
            icon={CircleInfoIcon}
            onClick={() => {}}
            style={{ marginLeft: '8px', color: HEX_646A7E }}
          />
        </div>
      ),
      dataKey: 'schema',
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {t('Claim Value')}
          <TooltippedIconButton
            data-testid="add-value-tooltip"
            label={t('Claim Value')}
            tooltipContent={t('Claim value for users who should be assigned the Tanium Account role.')}
            height={16}
            width={16}
            icon={CircleInfoIcon}
            onClick={() => {}}
            style={{ marginLeft: '8px', color: HEX_646A7E }}
          />
        </div>
      ),
      dataKey: 'value',
    },
  ];
  const roleRows = useMemo(() => {
    return idpRoles.map((role) => {
      return {
        userRole: mapRole(role),
        selected: (
          <Checkbox
            data-testid={`editable-value-checkbox-${role}`}
            checked={selectedRoleIds.has(role)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRoleIds(new Set([...selectedRoleIds, role]));
                const newRoleMapperData = { ...roleMapperData };
                newRoleMapperData[role] = {
                  key: '',
                  value: '',
                };
                setRoleMapperData(newRoleMapperData);
              } else {
                const newSelection = new Set(selectedRoleIds);
                newSelection.delete(role);
                setSelectedRoleIds(newSelection);

                const newRoleMapperData = { ...roleMapperData };
                delete newRoleMapperData[role];
                setRoleMapperData(newRoleMapperData);
              }
            }}
          />
        ),
        schema: selectedRoleIds.has(role) && (
          <TextInput
            data-testid={`editable-value-input-${role}-key`}
            style={{ width: '300px' }}
            value={roleMapperData?.[role]?.key}
            onChange={(e) => {
              const newRoleMapperData = { ...roleMapperData };
              newRoleMapperData[role] = {
                ...newRoleMapperData[role],
                key: e.target.value,
              };
              setRoleMapperData(newRoleMapperData);
            }}
            required={true}
          />
        ),
        value: selectedRoleIds.has(role) && (
          <TextInput
            data-testid={`editable-value-input-${role}-value`}
            style={{ width: '300px' }}
            value={roleMapperData?.[role]?.value}
            onChange={(e) => {
              const newRoleMapperData = { ...roleMapperData };
              newRoleMapperData[role] = {
                ...newRoleMapperData[role],
                value: e.target.value,
              };
              setRoleMapperData(newRoleMapperData);
            }}
            required={true}
          />
        ),
      };
    });
  }, [idpRoles, selectedRoleIds, roleMapperData, setSelectedRoleIds, setRoleMapperData]);

  useEffect(() => {
    setDisplayRows([...roleRows]);
  }, [roleRows, roleMapperData, setRoleMapperData]);

  const dataGetter = useCallback((i: number) => displayRows[i], [displayRows]);
  return (
    <Stack direction="column" itemSpacing={1}>
      <MediumSubtleText>
        <Trans
          i18nKey="Configure the user role assigned to users who sign in through this IDP."
          components={{
            docLink: <LinkText to={DocLinks.IDP_ROLE_ASSIGN} />,
          }}
        >
          {/* tests fail unless something is in here */}
          Configure the user role assigned to users who sign in through this IDP.
        </Trans>
      </MediumSubtleText>
      <IdentityProviderGroupDescription />
      <StackedRibbonListWrapper height="130px">
        <StackedRibbonListMinimal
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          columns={[{ dataKey: 'selected', label: '', width: '24px' }, ...displayColumns] as any}
          isItemLoaded={() => true}
          itemCount={displayRows.length}
          dataGetter={dataGetter}
          itemIdGetter={(i) => dataGetter(i).userRole}
          sort={[]}
          selection={selectedRoleIds}
        />
      </StackedRibbonListWrapper>
    </Stack>
  );
};
